import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, RotateCcw, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api.js";

/**
 * The look of the slides that carry ARTICLES — set once, for the brand.
 *
 * Not to be confused with the Social templates tab beside it, which is the
 * therapist spotlight: different renderer, different roles, different data.
 * This one is the carousel, the story, the single feed image and the TikTok
 * photo post — everything that comes out of a social pack.
 *
 * WHY IT EXISTS. Every size adjustment used to live on ONE variant's
 * `scale_json` and carried nowhere. Turning the logo down because the lockup was
 * too loud meant doing it again on the next pack, and the next, while every pack
 * nobody re-opened kept the old look. The brand decision belongs here; a variant
 * override stays possible and stays the exception.
 *
 * NOTHING HERE IS THE ARTICLE PIPELINE. The previews are mock decks, which is
 * the point: you are deciding how the format looks, not how one post looks.
 */

const FORMATS: { key: string; label: string; w: number; h: number; note: string }[] = [
  { key: "portrait", label: "פורטרט 4:5", w: 1080, h: 1350, note: "קרוסלה ותמונה בודדת באינסטגרם." },
  { key: "story", label: "סטורי 9:16", w: 1080, h: 1920, note: "סטורי באינסטגרם ובפייסבוק — וגם פוסטי התמונות של טיקטוק." },
  { key: "square", label: "ריבוע 1:1", w: 1080, h: 1080, note: "שמור לשימוש עתידי." },
];

const SIZE_CONTROLS: { key: string; label: string }[] = [
  { key: "kicker", label: "כותרת עליונה" },
  { key: "title", label: "כותרת" },
  { key: "body", label: "טקסט" },
  { key: "stat", label: "מספר" },
  { key: "list", label: "רשימה" },
  { key: "badge", label: "תג" },
  { key: "logo", label: "לוגו" },
  { key: "count", label: "מונה" },
];

/**
 * Where the bottom stack sits with no lift, per format. Mirrors `LIFT_BASE` in
 * the renderer, and is here only so the slider can say where the logo LANDS.
 * "1.20" told an operator nothing; "הלוגו ב-316px" is the thing being decided.
 */
const LIFT_BASE: Record<string, { wrap: number; logo: number }> = {
  portrait: { wrap: 196, logo: 62 },
  square: { wrap: 196, logo: 62 },
  story: { wrap: 268, logo: 96 },
};

/** What each format's chrome eats off the bottom. 0 where nothing does. */
const BOTTOM_CHROME: Record<string, { px: number; what: string }> = {
  story: { px: 300, what: "שורת התגובה של אינסטגרם" },
  portrait: { px: 0, what: "" },
  square: { px: 0, what: "" },
};

const LOGO_CHOICES: { id: string; label: string; hint: string }[] = [
  { id: "lockup", label: "לוגו מלא", hint: "הלוקאפ עם שורת התיוג." },
  { id: "mark", label: "סמל", hint: "בלי שורת התיוג — קריא בגדלים קטנים." },
  { id: "none", label: "בלי", hint: "בלי לוגו." },
];

/**
 * Mock decks, and there are several ON PURPOSE.
 *
 * Deck length follows the article: a rich piece fills a story to its cap of
 * four, a thin one makes three, and a carousel runs eight to ten. Tuning
 * against one specimen is how you discover on Instagram that the other shape
 * overflows. The long headline is the realistic worst case, not the pretty one.
 */
const MOCKS: { id: string; label: string; slides: Record<string, unknown>[] }[] = [
  {
    id: "rich",
    label: "מאמר עשיר",
    slides: [
      { role: "hook", kicker: "רפואה משלימה", title: "למה הכאב חוזר בכל פעם שאתם מפסיקים טיפול?" },
      { role: "answer", kicker: "התשובה", title: "כי הטיפול מרגיע את הסימפטום ולא את מה שמייצר אותו", body: "רוב הפרוטוקולים עובדים על השריר שכואב. מה שמחזיק את הכאב הוא לרוב משהו אחר לגמרי — יציבה, שינה, או עומס שלא ירד." },
      { role: "numbers", kicker: "מספרים", title: "מה המחקר מראה", stats: [{ value: "68%", unit: "דיווחו על שיפור אחרי שישה טיפולים" }, { value: "3", unit: "חודשים עד חזרה לתפקוד מלא" }] },
      { role: "checklist", kicker: "לפני שמזמינים", title: "ארבע שאלות למטפל", items: ["כמה זמן אתה עוסק בשיטה?", "מה קורה אם אין שיפור?", "האם יש מצבים שבהם לא תטפל?", "כמה טיפולים עד שנדע?"] },
      { role: "cta", kicker: "הצעד הבא", title: "מטפלים מוסמכים מחכים לכם באלטרנבי", badge: "חיפוש מטפל" },
    ],
  },
  {
    id: "thin",
    label: "מאמר דל",
    slides: [
      { role: "hook", kicker: "שאלה נפוצה", title: "האם זה מתאים לכל אחד?" },
      { role: "answer", kicker: "התשובה", title: "לא, ויש מצבים שבהם עוצרים", body: "מטפלת אחראית תשאל על ההיסטוריה הבריאותית לפני שהיא נוגעת." },
      { role: "cta", kicker: "הצעד הבא", title: "לבדוק התאמה באלטרנבי", badge: "חיפוש מטפל" },
    ],
  },
  {
    id: "long",
    label: "כותרת ארוכה במיוחד",
    slides: [
      { role: "hook", kicker: "מה שכדאי לדעת לפני שמזמינים טיפול ראשון", title: "למה כל כך הרבה אנשים מפסיקים אחרי הטיפול השני, ומה אפשר לעשות אחרת כבר מההתחלה?" },
      { role: "cta", kicker: "הצעד הבא", title: "מטפלים מוסמכים ברפואה משלימה, לפי אזור ולפי שיטה, מחכים לכם באלטרנבי", badge: "חיפוש מטפל" },
    ],
  },
];

export function ArticleSlidesTab() {
  const qc = useQueryClient();
  const [format, setFormat] = useState("portrait");
  const [mockId, setMockId] = useState("rich");
  const [safe, setSafe] = useState(true);
  const [nonce, setNonce] = useState(0);
  const [draft, setDraft] = useState<Record<string, Record<string, number | string | boolean>>>({});

  const saved = useQuery({ queryKey: ["slide-defaults"], queryFn: () => api.slideDefaults() });

  // The server's values are the starting point; the draft is what the sliders
  // move. Kept apart so "save" is a decision rather than a side effect of
  // dragging, and so a reload discards rather than persists an experiment.
  useEffect(() => {
    if (saved.data?.defaults) setDraft(saved.data.defaults as typeof draft);
  }, [saved.data]);

  const current = draft[format] ?? {};
  const fmt = FORMATS.find((f) => f.key === format)!;
  const mock = MOCKS.find((m) => m.id === mockId)!;

  // A story caps at four frames; a carousel shows the whole deck. The preview
  // has to obey the same rule the generator does or it is not a preview.
  const frames = useMemo(
    () => (format === "story" ? mock.slides.slice(0, 4) : mock.slides),
    [format, mock],
  );

  const set = (key: string, value: number | string | boolean) =>
    setDraft((d) => ({ ...d, [format]: { ...(d[format] ?? {}), [key]: value } }));

  /**
   * SAVES THIS FORMAT ONLY.
   *
   * It used to send the whole draft, and the button said so — "שמירה לכל
   * הפורמטים". That is a real difference, not a wording problem: edit the story,
   * wander into 4:5 and nudge something, go back and save, and the 4:5 nudge you
   * had not decided on is written too. A save is a decision about the format on
   * screen. Anything pending elsewhere stays pending, and is named below rather
   * than being carried along quietly.
   */
  const save = useMutation({
    mutationFn: () => api.saveSlideDefaults({ [format]: current }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["slide-defaults"] });
      setNonce((n) => n + 1);
    },
  });

  // Which other formats the operator has moved and not saved. Compared against
  // the server's copy, so switching a format back to its saved values clears it.
  const dirtyElsewhere = useMemo(() => {
    const server = (saved.data?.defaults ?? {}) as typeof draft;
    return FORMATS.filter(
      (f) => f.key !== format && JSON.stringify(draft[f.key] ?? {}) !== JSON.stringify(server[f.key] ?? {}),
    ).map((f) => f.label);
  }, [draft, saved.data, format]);

  const base = LIFT_BASE[format] ?? LIFT_BASE.portrait!;
  const liftPx = Number(current.lift ?? 0);
  const chrome = BOTTOM_CHROME[format] ?? { px: 0, what: "" };

  const previewW = format === "story" ? 150 : 190;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        הנראות של השקופיות שנושאות <strong className="text-ink">מאמרים</strong> — קרוסלה, סטורי, תמונה בודדת ופוסט
        תמונות בטיקטוק. מה שנקבע כאן חל על כל החבילות; ווריאנט בודד יכול עדיין לדרוס לעצמו בעמוד המאמר.
        התצוגות הן <strong className="text-ink">מאמרי דמה</strong>, כי כאן מחליטים איך הפורמט נראה ולא איך פוסט אחד נראה.
      </p>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        {FORMATS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFormat(f.key)}
            title={f.note}
            className={`rounded border px-3 py-1.5 ${
              format === f.key ? "border-brand-700 bg-brand-50 font-medium text-brand-800" : "border-line hover:bg-canvas"
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="text-muted">·</span>
        {MOCKS.map((m) => (
          <button
            key={m.id}
            onClick={() => setMockId(m.id)}
            className={`rounded border px-2 py-1.5 ${
              mockId === m.id ? "border-brand-700 bg-brand-50 text-brand-800" : "border-line hover:bg-canvas"
            }`}
          >
            {m.label}
          </button>
        ))}
        <label className="ms-auto flex items-center gap-1.5">
          <input type="checkbox" checked={safe} onChange={(e) => setSafe(e.target.checked)} />
          אזור בטוח
        </label>
      </div>

      <p className="text-[11px] text-muted">{fmt.note}</p>

      {/* The guide means two different things and must not look like one. */}
      {safe && (
        <p className="text-[11px] text-muted">
          {format === "story"
            ? "המסגרת הוורודה היא מה שאינסטגרם מכסה בעצמה — פסי ההתקדמות ושורת הפרופיל למעלה, שורת התגובה למטה. אל תשימו שם את הבקשה."
            : "המסגרת הכחולה היא שוליים קומפוזיציוניים בלבד. על פוסט בפיד אף פלטפורמה לא מציירת כלום מעל."}
        </p>
      )}

      {/* WRAPS rather than scrolls. A transformed iframe inside an
          `overflow-x-auto` container does not paint at all — the box stays blank
          while its document is fully loaded and styled, which is exactly what
          this looked like. The spotlight tab wraps, and it has always worked. */}
      <div className="flex flex-wrap gap-3 pb-2" dir="rtl">
        {frames.map((s, i) => (
          <div key={i} className="shrink-0">
            <div
              className="relative overflow-hidden rounded-lg border border-line bg-canvas"
              style={{ width: previewW, height: Math.round((previewW * fmt.h) / fmt.w) }}
            >
              <iframe
                title={`mock-${i}`}
                src={`${api.slideDefaultsPreviewUrl({ format, mock: mockId, index: i, total: frames.length, safe, scale: current })}&n=${nonce}`}
                scrolling="no"
                /* NOT lazy, unlike the pipeline's slide strip: that one can hold
                   eighteen 1080px documents at once and needed the throttle. This
                   holds five mock frames, and inside a horizontal scroller the lazy
                   ones never painted at all. */
                /* PINNED to the top-left, and the box is a positioning context.
                   The iframe's layout box is 1080px wide inside a ~190px window,
                   and in an RTL document an oversized box anchors to the RIGHT —
                   which put its left edge, the transform origin, 104px outside
                   the window. The document was loading and styling perfectly and
                   painting entirely off-screen. Absolute placement makes this
                   independent of writing direction instead of accidentally
                   correct in one of them. */
                style={{
                  position: "absolute",
                  insetInlineStart: "auto",
                  left: 0,
                  top: 0,
                  width: fmt.w,
                  height: fmt.h,
                  border: 0,
                  transform: `scale(${previewW / fmt.w})`,
                  transformOrigin: "top left",
                }}
              />
            </div>
            <p className="mt-1 text-center text-[10px] text-muted">
              {i + 1}. {String((s as { role?: string }).role ?? "")}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg border border-line p-3">
        {SIZE_CONTROLS.map((c) => (
          <label key={c.key} className="flex items-center gap-2 text-[11px]">
            <span className="w-20 shrink-0 text-muted">{c.label}</span>
            <input
              type="range" min={0.6} max={1.6} step={0.05}
              value={Number(current[c.key] ?? 1)}
              onChange={(e) => set(c.key, Number(e.target.value))}
              className="grow"
            />
            <span className="w-9 shrink-0 text-left font-mono text-muted">
              {Number(current[c.key] ?? 1).toFixed(2)}
            </span>
          </label>
        ))}
      </div>

      {/* Kept out of the grid above ON PURPOSE. Those are multipliers around 1;
          this is pixels, it moves things rather than resizing them, and putting
          it in the same row of sliders would read as one more size. */}
      <div className="rounded-lg border border-line p-3">
        <label className="flex items-center gap-2 text-[11px]">
          <span className="w-20 shrink-0 text-muted">מרחק מלמטה</span>
          <input
            type="range" min={0} max={400} step={5}
            value={liftPx}
            onChange={(e) => set("lift", Number(e.target.value))}
            className="grow"
          />
          <span className="w-14 shrink-0 text-left font-mono text-muted">{liftPx}px</span>
        </label>
        <p className="mt-1 text-[10px] text-muted">
          מרים את הטקסט, הלוגו והמונה ביחד. הלוגו יושב עכשיו{" "}
          <span className="font-mono text-ink">{base.logo + liftPx}px</span> מלמטה, הטקסט{" "}
          <span className="font-mono text-ink">{base.wrap + liftPx}px</span>.
          {chrome.px > 0 && (
            <>
              {" "}
              <span className={base.logo + liftPx < chrome.px ? "text-danger" : "text-emerald-700"}>
                {base.logo + liftPx < chrome.px
                  ? `${chrome.what} מכסה את ${chrome.px}px התחתונים — הלוגו מתחתיה.`
                  : `מעל ${chrome.what} (${chrome.px}px).`}
              </span>
            </>
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-[11px]">
        <span className="w-20 shrink-0 text-muted">לוגו</span>
        {LOGO_CHOICES.map((c) => (
          <button
            key={c.id}
            onClick={() => set("logoAsset", c.id)}
            title={c.hint}
            className={`rounded border px-2 py-1 ${
              (current.logoAsset ?? "lockup") === c.id
                ? "border-brand-700 bg-brand-50 text-brand-800"
                : "border-line hover:bg-canvas"
            }`}
          >
            {c.label}
          </button>
        ))}

        <label className="ms-4 flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={current.showCount !== false}
            onChange={(e) => set("showCount", e.target.checked)}
          />
          מונה שקופיות
          <span className="text-muted">
            {format === "story" ? "(בסטורי אינסטגרם כבר מציירת פסי התקדמות)" : ""}
          </span>
        </label>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
        >
          {save.isPending ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          שמירה — {fmt.label}
        </button>
        <button
          onClick={() => { if (saved.data?.defaults) setDraft(saved.data.defaults as typeof draft); setNonce((n) => n + 1); }}
          className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-xs"
        >
          <RotateCcw size={13} /> ביטול שינויים
        </button>
        {save.isSuccess && !save.isPending && (
          <span className="flex items-center gap-1 text-xs text-emerald-700">
            <CheckCircle2 size={13} /> נשמר — חבילות קיימות שלא נדרסו ידנית יקבלו את זה מיד
          </span>
        )}
        {save.isError && <span className="text-xs text-danger">{(save.error as Error).message}</span>}
      </div>

      {dirtyElsewhere.length > 0 && (
        <p className="text-[11px] text-amber-700">
          שינויים שלא נשמרו גם ב־{dirtyElsewhere.join(", ")} — עוברים לפורמט ושומרים אותו בנפרד.
        </p>
      )}

      {/* Said plainly, because it is the one surprising consequence. */}
      <p className="text-[11px] text-muted">
        שינוי כאן משפיע גם על חבילות שכבר נוצרו, כי השקופית נבנית בזמן אמת. פוסט
        שכבר <strong className="text-ink">נשלח</strong> לפוסטיז לא ישתנה — התמונה שלו כבר הועלתה.
      </p>
    </div>
  );
}
